import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import { Eye, EyeOff, Lock, Mail, Shield } from "lucide-react";
import apiClient from "../../utils/apiClient";
import CommonToaster from "../Common/CommonToaster";
import AuthStepProgress from "./AuthStepProgress";
import { AppButton, Callout, FormField, IconButton, Input, PhoneInput } from "../ui";

// The three steps, and everything that differs between them. Keeping the copy
// in one table is what stops the heading, the sub-line and the button label
// drifting out of step with each other.
const STEPS = [
  {
    title: "Reset Password",
    sub: "Enter your details and we'll send you an OTP",
    action: "Send OTP",
    busy: "Sending OTP…",
  },
  {
    title: "Verify OTP",
    sub: "Enter the verification code sent to your email",
    action: "Verify OTP",
    busy: "Verifying…",
  },
  {
    title: "Create New Password",
    sub: "Choose a strong and secure new password",
    action: "Reset Password",
    busy: "Saving…",
  },
];

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastType, setToastType] = useState("success");
  const [step, setStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const navigate = useNavigate();

  const mismatch = Boolean(confirmPassword) && newPassword !== confirmPassword;
  const copy = STEPS[step - 1];

  const showToastMessage = (message, type = "success") => {
    if (type === "success") {
      setSuccess(message);
      setError("");
    } else {
      setError(message);
      setSuccess("");
    }
    setToastType(type);
    setShowToast(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      if (step === 1) {
        const response = await apiClient.post("/auth/forgot-password", { email, mobile });
        if (response.data.code === 200) {
          showToastMessage("OTP sent to your email. Please check your inbox.", "success");
          setStep(2);
        } else {
          showToastMessage("Failed to send OTP. Please try again.", "error");
        }
      } else if (step === 2) {
        const response = await apiClient.post("/auth/verify-otp", { email, otp });
        if (response.data.code === 200) {
          // Capture the reset token — change-password now requires it as proof
          // that the OTP was verified (an email alone can no longer reset).
          setResetToken(response.data.resetToken || "");
          showToastMessage("OTP verified successfully!", "success");
          setStep(3);
        } else {
          showToastMessage("Invalid OTP", "error");
        }
      } else if (step === 3) {
        if (newPassword === confirmPassword) {
          await apiClient.post("/auth/change-password", { email, newPassword, resetToken });
          showToastMessage("Password changed successfully! Redirecting to login...", "success");
          setTimeout(() => navigate("/login"), 2000);
        } else {
          showToastMessage("Passwords do not match", "error");
        }
      }
    } catch (err) {
      showToastMessage(
        err?.response?.data?.message || "An error occurred. Please try again.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div>
        <header className="tw-mb-6 tw-text-center">
          <h1 className="tw-mb-1.5 tw-text-[1.75rem] tw-font-bold tw-tracking-[-0.02em] tw-text-content">
            {copy.title}
          </h1>
          <p className="tw-mb-0 tw-text-sm tw-text-content-muted">{copy.sub}</p>
        </header>

        <AuthStepProgress total={STEPS.length} current={step} className="tw-mb-7" />

        <form onSubmit={handleSubmit} className="tw-flex tw-flex-col tw-gap-4">
          {step === 1 && (
            <>
              <FormField label="Email Address" htmlFor="fp-email">
                <Input
                  id="fp-email"
                  size="lg"
                  type="email"
                  autoComplete="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  leading={<Mail size={16} />}
                />
              </FormField>
              <FormField label="Mobile Number" htmlFor="fp-mobile">
                <PhoneInput
                  id="fp-mobile"
                  size="lg"
                  placeholder="Enter your mobile number"
                  value={mobile}
                  onChange={setMobile}
                  required
                />
              </FormField>
            </>
          )}

          {step === 2 && (
            <FormField label="Verification Code" htmlFor="fp-otp">
              <Input
                id="fp-otp"
                size="lg"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="Enter 6-digit OTP"
                maxLength={6}
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                required
                className="tw-font-semibold tw-tracking-[0.3em]"
                leading={<Shield size={16} />}
              />
            </FormField>
          )}

          {step === 3 && (
            <>
              <FormField label="New Password" htmlFor="fp-new-password">
                <Input
                  id="fp-new-password"
                  size="lg"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  placeholder="Enter new password"
                  minLength={5}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  leading={<Lock size={16} />}
                  trailing={
                    <IconButton
                      size="sm"
                      label={showPassword ? "Hide password" : "Show password"}
                      icon={showPassword ? Eye : EyeOff}
                      onClick={() => setShowPassword((v) => !v)}
                      tabIndex={-1}
                    />
                  }
                />
              </FormField>
              <FormField
                label="Confirm Password"
                htmlFor="fp-confirm-password"
                error={mismatch ? "Passwords do not match" : null}
              >
                <Input
                  id="fp-confirm-password"
                  size="lg"
                  type={showConfirm ? "text" : "password"}
                  autoComplete="new-password"
                  placeholder="Confirm new password"
                  minLength={5}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  invalid={mismatch}
                  leading={<Lock size={16} />}
                  trailing={
                    <IconButton
                      size="sm"
                      label={showConfirm ? "Hide password" : "Show password"}
                      icon={showConfirm ? Eye : EyeOff}
                      onClick={() => setShowConfirm((v) => !v)}
                      tabIndex={-1}
                    />
                  }
                />
              </FormField>
            </>
          )}

          {(error || success) && (
            <Callout tone={error ? "danger" : "success"} role="alert">
              {error || success}
            </Callout>
          )}

          <AppButton type="submit" size="lg" className="tw-h-12 tw-w-full" disabled={loading}>
            {loading ? (
              <>
                <Spinner size="sm" animation="border" /> {copy.busy}
              </>
            ) : (
              copy.action
            )}
          </AppButton>
        </form>

        <p className="tw-mb-0 tw-mt-6 tw-text-center tw-text-sm tw-text-content-muted">
          Remember your password?{" "}
          <Link
            to="/login"
            className="tw-font-semibold tw-text-primary tw-no-underline hover:tw-underline"
          >
            Back to sign in
          </Link>
        </p>
      </div>

      <CommonToaster
        show={showToast}
        message={success || error}
        type={toastType}
        onClose={() => setShowToast(false)}
      />
    </>
  );
};

export default ForgotPassword;
